import { Link, useNavigate } from "react-router-dom"
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Slide,
  Button,
  Fab,
  useTheme,
  Paper,
  CardMedia,
} from "@mui/material"
import _ from "lodash"
import { Trans } from "react-i18next"
import { isValidURL } from "utils/url"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { sanitizePublicPath } from "providers/venue/utils/fetchUtils"
import { TopBarContainer } from "containers/TopBarContainer"
import {
  BackIcon,
  PrevStepIcon,
  NextStepIcon,
  CloseIcon,
  PlayArrowIcon,
  RefreshArrowIcon,
  PauseIcon,
} from "components/icons"
import { VerticalDirectionImageDialog } from "components/Dialog/VerticalDirectionImageDialog"
import { StepProgressBar } from "components/Progress/StepProgressBar"
import { OrdinalSelector } from "components/OrdinalSelector"
import { ExitIcon } from "components/icons"
import { useStepByStep } from "hooks/direction/useStepByStep"
import { useCallback, useMemo, useRef, useState } from "react"
import { useStepByStepMap } from "hooks/direction/useStepByStepMap"
import { getIcon } from "utils/icon"
import { CoolDownProgress } from "components/CoolDownProgress"
import { deepmerge } from "@mui/utils"

export const StepByStep = () => {
  const navigate = useNavigate()
  const { isMultiOrdinalVenue } = useVenue()
  const contentRef = useRef(null)

  const [slideEntered, setSlideEntered] = useState(false)
  const offsetBottom = +contentRef.current?.clientHeight || 0
  const {
    route,
    steps,
    currentStepNumber,
    currentStep,
    currentOriginStepLevelName,
    currentDestStepLevelName,
    currentLevel,
    currentOrdinal,
    originName,
    originVenueLogo,
    originVenueData,
    currentVerticalStepMethod,
    destinationName,
    destinationVenueLogo,
    destinationVenueData,
    isReady,
    hasNextStep,
    hasPrevStep,
    exitPath,
    goToStepByIndex,
    goToPrevStep,
    goToNextStep,
    stepAutoplayConfig,
    restartAutoPlay,
    resumeAutoPlay,
    pauseAutoPlay,
    isPlaying,
    directionOverviewPath,
  } = useStepByStep()

  const stepsAvailable = steps && steps.length > 0

  useStepByStepMap(route, currentStepNumber, {
    offset: { bottom: offsetBottom },
    enabled: isReady && slideEntered,
  })

  const { t } = useTranslation()

  const theme = useTheme()

  const {
    container,
    primaryText,
    title,
    locationText,
    navButtonWrapper,
    headerWrapper,
    navButton,
    iconNavButton,
    overviewWrapper,
    wrapper,
    kioskBackButton,
    highlightedText,
    stepTextContainer,
    exitButtonWrapper,
    exitButton,
    stepProgressBar,
    stepDetailStack,
    venueLogowrapper,
    closeButton,
    playerControls,
    playerWrapper,
    actionButtonWrapper,
    playerButton,
    originIconContainer,
    destinationIconContainer,
    cooldownProgress,
  } = theme.components[`AppNavigationStep`]?.styleOverrides || {}
  const { root: topBarRoot, ordinalSelector } =
    theme.components[`AppTopBar`]?.styleOverrides || {}

  const highlightedStepText = (
    <Typography variant="subTitle" component="span" sx={highlightedText} />
  )

  const handleSlideEnter = useCallback(
    () => !slideEntered && setSlideEntered(true),
    [slideEntered]
  )

  const verticalDirectionImageUrl = useMemo(() => {
    const src = _.get(
      theme?.assets?.verticalStep?.[currentVerticalStepMethod],
      "url",
      ""
    )
    return isValidURL(src) ? src : sanitizePublicPath(src)
  }, [currentVerticalStepMethod, theme?.assets?.verticalStep])

  const showVerticalStepDialog = !!verticalDirectionImageUrl

  const prevStepIcon = useMemo(
    () =>
      getIcon(theme?.assets?.navigationStep?.prevIcon) || (
        <PrevStepIcon fontSize="inherit" />
      ),
    [theme]
  )
  const nextStepIcon = useMemo(
    () =>
      getIcon(theme?.assets?.navigationStep?.nextIcon) || (
        <NextStepIcon fontSize="inherit" />
      ),
    [theme]
  )

  const backIcon = useMemo(
    () => getIcon(theme?.assets?.backIcon) || <BackIcon />,
    [theme]
  )

  const closeIcon = useMemo(
    () => getIcon(theme?.assets?.directionStep?.closeIcon) || <CloseIcon />,
    [theme]
  )

  const playStepIcon = useMemo(
    () =>
      getIcon(theme?.assets?.playStep?.nextIcon) || (
        <PlayArrowIcon fontSize="inherit" />
      ),
    [theme]
  )
  const playAgainStepIcon = useMemo(
    () =>
      getIcon(theme?.assets?.playStep?.playAgainStepIcon) || (
        <RefreshArrowIcon fontSize="inherit" />
      ),
    [theme]
  )
  const pauseStepIcon = useMemo(
    () =>
      getIcon(theme?.assets?.playStep?.pauseStepIcon) || (
        <PauseIcon fontSize="inherit" />
      ),
    [theme]
  )

  const originVenueRef = _.get(originVenueData, "reference")
  const destinationVenueRef = _.get(destinationVenueData, "reference")

  const handleClickExit = useCallback(() => {
    navigate(directionOverviewPath, {
      replace: true,
    })
  }, [navigate, directionOverviewPath])

  return (
    <>
      <VerticalDirectionImageDialog
        imageSrc={verticalDirectionImageUrl}
        description={currentStep?.description}
        originLevelName={currentOriginStepLevelName}
        destinationLevelName={currentDestStepLevelName}
        open={showVerticalStepDialog}
      />
      {/* Header Section */}
      <TopBarContainer sx={topBarRoot}>
        <Stack spacing={5}>
          {/* only show ordinalSelector when there is no VerticalDirectionImageDialog showing */}
          {isMultiOrdinalVenue && !showVerticalStepDialog && (
            <Box sx={ordinalSelector}>
              <OrdinalSelector
                label="Floor"
                levels={currentLevel}
                selectedOrdinal={currentOrdinal}
              />
            </Box>
          )}
        </Stack>
      </TopBarContainer>
      {/* End of Header Section */}
      <Slide direction="up" in={true} onEnter={handleSlideEnter}>
        <Box sx={wrapper}>
          {/* TODO: will remove? */}
          <Fab variant="rounded" onClick={handleClickExit} sx={kioskBackButton}>
            {backIcon}
          </Fab>
          <Paper sx={container} ref={contentRef}>
            <Box sx={exitButtonWrapper}>
              <Button
                component={Link}
                to={exitPath}
                startIcon={<ExitIcon />}
                sx={exitButton}
                variant="contained"
              >
                {t("Exit")}
              </Button>
            </Box>
            <Stack width="100%" direction="column" sx={stepDetailStack}>
              <Box sx={headerWrapper}>
                <Stack alignItems="center" spacing={1}>
                  <Stack sx={actionButtonWrapper}>
                    <IconButton
                      color="inherit"
                      sx={iconNavButton}
                      onClick={goToPrevStep}
                      disabled={!hasPrevStep}
                    >
                      {prevStepIcon}
                    </IconButton>

                    <Box sx={playerWrapper}>
                      <Box sx={playerControls}>
                        {isPlaying ? (
                          <IconButton
                            sx={deepmerge(iconNavButton, playerButton)}
                            onClick={pauseAutoPlay}
                            disabled={!isPlaying}
                          >
                            {pauseStepIcon}
                          </IconButton>
                        ) : (
                          <IconButton
                            sx={deepmerge(iconNavButton, playerButton)}
                            onClick={
                              hasNextStep ? resumeAutoPlay : restartAutoPlay
                            }
                          >
                            {hasNextStep ? playStepIcon : playAgainStepIcon}
                          </IconButton>
                        )}
                      </Box>

                      <Typography component="h1" textAlign="center" sx={title}>
                        {t("Process step", {
                          currentStep: currentStepNumber,
                          totalSteps: steps.length,
                        })}
                      </Typography>
                    </Box>

                    <IconButton
                      color="inherit"
                      sx={iconNavButton}
                      onClick={goToNextStep}
                      disabled={!hasNextStep}
                    >
                      {nextStepIcon}
                    </IconButton>
                  </Stack>
                  <Box sx={stepTextContainer}>
                    <Typography
                      variant="subTitle"
                      component="p"
                      sx={primaryText}
                    >
                      <Trans
                        components={{
                          category: highlightedStepText,
                          destination: highlightedStepText,
                          landmark: highlightedStepText,
                          intermediary: highlightedStepText,
                          toward: highlightedStepText,
                          turn: highlightedStepText,
                        }}
                      >
                        {t(currentStep?.description.text, {
                          category: t(
                            currentStep?.description.options?.category
                          ),
                          destination: t(
                            currentStep?.description.options?.destination
                          ),
                          landmark: t(
                            currentStep?.description.options?.landmark
                          ),
                          intermediary: t(
                            currentStep?.description.options?.intermediary
                          ),
                          toward: t(currentStep?.description.options?.toward),
                          turn: t(currentStep?.description.options?.turn),
                        })}
                      </Trans>
                    </Typography>
                  </Box>
                </Stack>

                <Button
                  onClick={handleClickExit}
                  sx={closeButton}
                  variant="contained"
                >
                  {closeIcon}
                </Button>
              </Box>
              {/* mobile progress bar */}
              <StepProgressBar
                steps={steps}
                currentStep={currentStepNumber}
                sx={stepProgressBar}
                handleClickStep={goToStepByIndex}
                enableAnimateScroll={slideEntered && stepsAvailable}
              />
              {/* end of mobile progress bar */}

              {/* Location and overview section */}
              <Stack sx={overviewWrapper}>
                <Box sx={originIconContainer}>
                  <Box sx={venueLogowrapper} className={originVenueRef}>
                    <CardMedia image={originVenueLogo} />
                  </Box>
                  <Typography
                    textAlign="start"
                    variant="caption"
                    sx={locationText}
                  >
                    {t(originName)}
                  </Typography>
                </Box>
                <Box sx={destinationIconContainer}>
                  <Box sx={venueLogowrapper} className={destinationVenueRef}>
                    <CardMedia image={destinationVenueLogo} />
                  </Box>
                  <Typography
                    textAlign="end"
                    variant="caption"
                    sx={locationText}
                  >
                    {t(destinationName)}
                  </Typography>
                </Box>
              </Stack>

              <Stack sx={navButtonWrapper}>
                <Button
                  variant="outlined"
                  startIcon={<PrevStepIcon />}
                  disabled={!hasPrevStep}
                  sx={navButton}
                  onClick={goToPrevStep}
                >
                  {t("Previous Step")}
                </Button>
                <Button
                  variant="outlined"
                  disabled={!hasNextStep}
                  sx={navButton}
                  endIcon={<NextStepIcon />}
                  onClick={goToNextStep}
                >
                  {t("Next Step")}
                </Button>
              </Stack>
            </Stack>
            {isPlaying && (
              <CoolDownProgress
                sx={cooldownProgress}
                duration={stepAutoplayConfig.delay}
                repeat={true}
                reverse={true}
              />
            )}
          </Paper>
        </Box>
      </Slide>
    </>
  )
}
